import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

const NOTE_COLORS = ["#FAE3E8", "#E6E1F4", "#DDE5D6", "#F6EBD4"];
const STORAGE_KEY = "keepsake-wishes";

const seed = [
  { text: "Happy birthday! So glad you two found your way back.", color: 0 },
  { text: "To many more concerts and zero more drifts.", color: 1 },
];

export const WishesBoard = () => {
  const [notes, setNotes] = useState(seed);
  const [draft, setDraft] = useState("");
  const [color, setColor] = useState(0);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (Array.isArray(saved)) setNotes(saved);
    } catch { /* use seed */ }
  }, []);

  const persist = (next) => {
    setNotes(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const add = () => {
    if (!draft.trim()) return;
    persist([...notes, { text: draft.trim(), color }]);
    setDraft("");
  };

  return (
    <section data-testid="wishes-board-section" className="px-6 py-24 sm:py-32">
      <div className="mx-auto max-w-4xl">
        <div className="text-center">
          <p className="font-hand text-2xl text-[#C87D87]">leave her something</p>
          <h2 className="mt-3 font-display text-2xl font-semibold sm:text-3xl lg:text-4xl">The Birthday Wishes Board</h2>
          <p className="mx-auto mt-4 max-w-md text-sm text-[#4A2E35]/60">
            Pin a note for her to find. They stay saved on this device.
          </p>
        </div>

        <div className="paper-card deckle mx-auto mt-12 max-w-xl p-6 sm:p-8">
          <textarea
            data-testid="interactive-note-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Write a birthday note…"
            rows={3}
            maxLength={140}
            className="w-full resize-none rounded-xl border border-[#E8B4B8]/50 bg-[#FAF6EE] p-4 font-hand text-xl outline-none placeholder:text-[#4A2E35]/35 focus:border-[#C87D87]"
          />
          <div className="mt-4 flex items-center justify-between">
            <div className="flex gap-2">
              {NOTE_COLORS.map((c, i) => (
                <button
                  key={c}
                  data-testid={`note-color-${i}`}
                  onClick={() => setColor(i)}
                  aria-label={`Note color ${i + 1}`}
                  className={`h-7 w-7 rounded-full border-2 transition-transform ${color === i ? "scale-110 border-[#4A2E35]" : "border-transparent"}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <button
              data-testid="interactive-note-submit-btn"
              onClick={add}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#C87D87] px-5 py-2.5 text-xs font-semibold text-[#FFFDF9] transition-transform hover:scale-105 active:scale-95"
            >
              <Plus size={14} /> Pin Note
            </button>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap justify-center gap-5">
          {notes.map((n, i) => (
            <div
              key={i}
              data-testid="wish-note"
              className="group relative w-44 rounded-lg p-4 pt-6 shadow-md transition-transform hover:-translate-y-1 hover:rotate-0"
              style={{ backgroundColor: NOTE_COLORS[n.color % NOTE_COLORS.length], transform: `rotate(${(i % 2 ? 1 : -1) * 2}deg)` }}
            >
              <span className="tape" style={{ width: 50, height: 18, top: -9 }} />
              <p className="font-hand text-lg leading-snug text-[#4A2E35]/85">{n.text}</p>
              <button
                data-testid={`wish-note-delete-${i}`}
                onClick={() => persist(notes.filter((_, j) => j !== i))}
                aria-label="Delete note"
                className="absolute bottom-2 right-2 rounded-full p-1 text-[#4A2E35]/30 opacity-0 transition-opacity hover:text-[#C87D87] group-hover:opacity-100"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
